import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../Card';
import { Input, InputWrapper, Label, TextArea } from '../Input';
import {
  useBrandContext,
  SectionGrid,
  FormGroup,
  SectionHeaderRow,
  AiFillButton,
  TagInput,
  Tag,
  TagRemove,
  TagInputField,
  SubSection,
  SubSectionTitle,
  makeTagKeyDown,
} from './brandShared';

/**
 * Tab "Identity" del Brand page unificado.
 *
 * Tres bloques:
 *  - Brand Identity (overview, core, positioning, audience, profile + competitors tags)
 *  - Brand Personality (personality, story, benefits + products/services tags + service areas/phone)
 *  - Contact: superficie los campos orphan (email, whatsapp, address, businessHours)
 *
 * Todo se enlaza a form.formData via setField / addTag / removeTag. Sin estado
 * de persistencia propio: el guardado global lo maneja el form del contexto.
 */
export function BrandTabIdentity() {
  const { form } = useBrandContext();
  const { formData, setField, addTag, removeTag, openAiFill } = form;

  // Inputs locales de los tag chips (Enter o coma agregan).
  const [competitorsInput, setCompetitorsInput] = useState('');
  const [productsInput, setProductsInput] = useState('');

  return (
    <SectionGrid>
      {/* ─── Brand Identity ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <SectionHeaderRow>
            <div>
              <CardTitle>Brand Identity</CardTitle>
              <CardDescription>Core information about your brand</CardDescription>
            </div>
            <AiFillButton type="button" onClick={() => openAiFill('identity')} title="Search with AI">
              <span aria-hidden="true">✨</span> AI
            </AiFillButton>
          </SectionHeaderRow>
        </CardHeader>
        <CardContent>
          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-business-overview">Business Overview</Label>
              <TextArea
                id="brand-business-overview"
                value={formData.businessOverview || ''}
                onChange={(e) => setField('businessOverview', e.target.value)}
                placeholder="e.g., We are a boutique digital marketing agency specializing in helping local restaurants and cafes grow their online presence through social media and email marketing."
                rows={3}
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-core-identity">Core Identity</Label>
              <TextArea
                id="brand-core-identity"
                value={formData.coreIdentity || ''}
                onChange={(e) => setField('coreIdentity', e.target.value)}
                placeholder="e.g., Our mission is to make premium skincare accessible to everyone. We value transparency, sustainability, and science-backed ingredients."
                rows={2}
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-market-positioning">Market Positioning</Label>
              <TextArea
                id="brand-market-positioning"
                value={formData.marketPositioning || ''}
                onChange={(e) => setField('marketPositioning', e.target.value)}
                placeholder="e.g., We position ourselves as the affordable premium option — higher quality than mass-market brands but more accessible than luxury competitors like La Mer."
                rows={2}
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-target-audience">Target Audience</Label>
              <TextArea
                id="brand-target-audience"
                value={formData.targetAudience || ''}
                onChange={(e) => setField('targetAudience', e.target.value)}
                placeholder="e.g., Health-conscious women aged 25-45 who care about clean ingredients and are willing to invest in their skincare routine."
                rows={2}
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-customer-profile">Customer Profile</Label>
              <TextArea
                id="brand-customer-profile"
                value={formData.customerProfile || ''}
                onChange={(e) => setField('customerProfile', e.target.value)}
                placeholder="e.g., Sarah, 32, works in marketing, shops at Sephora, follows skincare influencers on Instagram, reads ingredient labels, prefers cruelty-free products."
                rows={2}
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-competitors">Competitors (press Enter to add)</Label>
              <TagInput>
                {formData.competitors?.map((item, i) => (
                  <Tag key={i}>
                    {item}
                    <TagRemove
                      type="button"
                      aria-label={`Remove ${item}`}
                      onClick={() => removeTag('competitors', i)}
                    >
                      <span aria-hidden="true">×</span>
                    </TagRemove>
                  </Tag>
                ))}
                <TagInputField
                  id="brand-competitors"
                  value={competitorsInput}
                  onChange={(e) => setCompetitorsInput(e.target.value)}
                  onKeyDown={makeTagKeyDown(
                    (v) => addTag('competitors', v),
                    competitorsInput,
                    () => setCompetitorsInput(''),
                  )}
                  placeholder="e.g., CeraVe, The Ordinary..."
                />
              </TagInput>
            </InputWrapper>
          </FormGroup>
        </CardContent>
      </Card>

      {/* ─── Brand Personality ──────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <SectionHeaderRow>
            <div>
              <CardTitle>Brand Personality</CardTitle>
              <CardDescription>Your brand&apos;s character and story</CardDescription>
            </div>
            <AiFillButton type="button" onClick={() => openAiFill('personality')} title="Search with AI">
              <span aria-hidden="true">✨</span> AI
            </AiFillButton>
          </SectionHeaderRow>
        </CardHeader>
        <CardContent>
          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-personality">Brand Personality</Label>
              <TextArea
                id="brand-personality"
                value={formData.brandPersonality || ''}
                onChange={(e) => setField('brandPersonality', e.target.value)}
                placeholder="e.g., Warm and approachable, like a knowledgeable friend. We're confident but never arrogant, playful but professional."
                rows={2}
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-story">Brand Story</Label>
              <TextArea
                id="brand-story"
                value={formData.brandStory || ''}
                onChange={(e) => setField('brandStory', e.target.value)}
                placeholder="e.g., Founded in 2019 by a dermatologist frustrated with overpriced products filled with fillers. Started in a home lab, now sold in 500+ stores."
                rows={3}
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-emotional-benefits">Emotional Benefits</Label>
              <TextArea
                id="brand-emotional-benefits"
                value={formData.emotionalBenefits || ''}
                onChange={(e) => setField('emotionalBenefits', e.target.value)}
                placeholder="e.g., Customers feel empowered and confident knowing they're using clean, effective products. They feel smart for choosing quality over hype."
                rows={2}
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-products-services">Products/Services (press Enter to add)</Label>
              <TagInput>
                {formData.productsServices?.map((item, i) => (
                  <Tag key={i}>
                    {item}
                    <TagRemove
                      type="button"
                      aria-label={`Remove ${item}`}
                      onClick={() => removeTag('productsServices', i)}
                    >
                      <span aria-hidden="true">×</span>
                    </TagRemove>
                  </Tag>
                ))}
                <TagInputField
                  id="brand-products-services"
                  value={productsInput}
                  onChange={(e) => setProductsInput(e.target.value)}
                  onKeyDown={makeTagKeyDown(
                    (v) => addTag('productsServices', v),
                    productsInput,
                    () => setProductsInput(''),
                  )}
                  placeholder="e.g., Facial Serum, Moisturizer..."
                />
              </TagInput>
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-service-areas">Service Areas</Label>
              <Input
                id="brand-service-areas"
                value={formData.serviceAreas || ''}
                onChange={(e) => setField('serviceAreas', e.target.value)}
                placeholder="e.g., United States, Canada, UK — ships worldwide"
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <InputWrapper>
              <Label htmlFor="brand-phone-number">Phone Number</Label>
              <Input
                id="brand-phone-number"
                value={formData.phoneNumber || ''}
                onChange={(e) => setField('phoneNumber', e.target.value)}
                placeholder="Contact phone number..."
              />
            </InputWrapper>
          </FormGroup>

          {/* Contact — superficie los campos orphan que antes no tenían UI.
              Son datos REALES usados en posters baked-AI (nunca inventados). */}
          <SubSection>
            <SubSectionTitle>Contact</SubSectionTitle>

            <FormGroup>
              <InputWrapper>
                <Label htmlFor="brand-email">Email</Label>
                <Input
                  id="brand-email"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setField('email', e.target.value)}
                  placeholder="e.g., hello@your-brand.com"
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label htmlFor="brand-whatsapp">WhatsApp</Label>
                <Input
                  id="brand-whatsapp"
                  value={formData.whatsapp || ''}
                  onChange={(e) => setField('whatsapp', e.target.value)}
                  placeholder="e.g., +1 555 123 4567"
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label htmlFor="brand-address">Address</Label>
                <Input
                  id="brand-address"
                  value={formData.address || ''}
                  onChange={(e) => setField('address', e.target.value)}
                  placeholder="e.g., 123 Main St, Springfield, IL"
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label htmlFor="brand-business-hours">Business Hours</Label>
                <Input
                  id="brand-business-hours"
                  value={formData.businessHours || ''}
                  onChange={(e) => setField('businessHours', e.target.value)}
                  placeholder="e.g., Mon-Fri 9am-6pm, Sat 10am-2pm"
                />
              </InputWrapper>
            </FormGroup>
          </SubSection>
        </CardContent>
      </Card>
    </SectionGrid>
  );
}
